import React, { useMemo, useState } from 'react';
import {
  Box, Text, HStack, VStack, Icon, Pressable, ScrollView,
} from '@gluestack-ui/themed';
import {
  FileText, AlignLeft, Eye, NotebookText, Search, Blocks, Sparkles, ChevronDown, ChevronUp,
} from 'lucide-react-native';
import { LinearGradient } from 'expo-linear-gradient';

import HtmlText from '@/common/components/HtmlText';
import { useTenantGradingSettings } from '@/common/context/TenantSettingsStore';
import type { Task } from '@/common/api/models/task';
import type { DimObjectives } from '@/common/api/models/dimObjectives';
import type { DimensionCriterion } from './types';
import EvalJustification from './EvalJustification';

// Icon mapping matching student TaskObjectives
const getDimensionIcon = (dimension: string) => {
  const iconMap: Record<string, React.ElementType> = {
    description: NotebookText,
    analysis: Search,
    application: Blocks,
    formal: AlignLeft,
    reflection: Eye,
  };
  return iconMap[dimension] || FileText;
};

interface AutogradeReasoningProps {
  autogradeData: Record<string, string> | null;
  dimensionColors: Record<string, string>;
  dimensionCriteria: Record<string, DimensionCriterion[]>;
  applicableDims: string[];
}

/**
 * Displays AI grading feedback per dimension using JustificationBadges (the pill elements)
 */
const AutogradeReasoning: React.FC<AutogradeReasoningProps> = ({
  autogradeData, dimensionColors, dimensionCriteria, applicableDims,
}) => {
  if (!autogradeData) return null;

  const dimensions = applicableDims.filter(
    (dim) => autogradeData[dim] && typeof autogradeData[dim] === 'string' && autogradeData[dim].length > 0,
  );

  if (dimensions.length === 0) return null;

  return (
    <VStack space="md" pb="$2">
      {dimensions.map((dim) => {
        const justification = autogradeData[dim];
        const color = dimensionColors[dim] || '#666666';
        const criteria = dimensionCriteria?.[dim] || [];

        return (
          <VStack key={dim} space="xs" pl="$1">
            <Text size="xs" fontWeight="bold" color={color} textTransform="uppercase" letterSpacing={0.5}>
              {dim}
            </Text>
            <Box pl="$1">
              <EvalJustification
                justification={justification}
                dimension={dim}
                dimensionCriteria={criteria}
              />
            </Box>
          </VStack>
        );
      })}
    </VStack>
  );
};

interface Objective {
  dimension: string;
  text: string;
  key: string;
  color: string;
}

interface ObjectivesListProps {
  objectives: Objective[];
}

/**
 * Displays learning objectives
 */
const ObjectivesList: React.FC<ObjectivesListProps> = ({ objectives }) => {
  if (!objectives || objectives.length === 0) return null;

  return (
    <VStack space="sm" pb="$2">
      {objectives.map((obj) => {
        const IconComponent = getDimensionIcon(obj.dimension);
        return (
          <HStack key={obj.key} alignItems="flex-start" space="sm">
            <Box mt="$1">
              <Icon as={IconComponent} size="xs" color={obj.color} />
            </Box>
            <Text size="sm" color="$textBody" flex={1} lineHeight="$md">
              {obj.text}
            </Text>
          </HStack>
        );
      })}
    </VStack>
  );
};

/**
 * Section header with collapse toggle
 */
interface SectionHeaderProps {
  icon: React.ElementType;
  iconColor?: string;
  title: string;
  isExpanded: boolean;
  onToggle: () => void;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({
  icon, iconColor = '$textSubtle', title, isExpanded, onToggle,
}) => (
  <Pressable onPress={onToggle}>
    <HStack alignItems="center" justifyContent="space-between" pr="$2">
      <HStack alignItems="center" space="sm">
        <Icon as={icon} size="sm" color={iconColor} />
        <Text
          size="xs"
          color="$textSubtle"
          fontWeight="bold"
          textTransform="uppercase"
          letterSpacing={1}
        >
          {title}
        </Text>
      </HStack>
      <Icon as={isExpanded ? ChevronUp : ChevronDown} size="xs" color="$textHint" />
    </HStack>
  </Pressable>
);

/**
 * Scrollable content area with optional fade-out gradient
 */
interface ScrollableContentProps {
  children: React.ReactNode;
  showFade?: boolean;
}

// Fade gradient style for LinearGradient (requires plain object, not Gluestack tokens)
const fadeGradientStyle = {
  position: 'absolute' as const,
  bottom: 0,
  left: 0,
  right: 0,
  height: 32,
  pointerEvents: 'none' as const,
};

const ScrollableContent: React.FC<ScrollableContentProps> = ({ children, showFade = true }) => {
  if (!showFade) {
    return (
      <Box>
        {children}
      </Box>
    );
  }

  return (
    <Box flex={1}>
      <ScrollView showsVerticalScrollIndicator={false}>
        <Box>
          {children}
        </Box>
      </ScrollView>
      {/* eslint-disable-next-line react-native/no-inline-styles */}
      <LinearGradient colors={['rgba(255,255,255,0)', 'rgba(255,255,255,1)']} style={fadeGradientStyle} />
    </Box>
  );
};

interface TaskContextPreviewProps {
  task: Task;
  /** When omitted, legacy callers show all tenant dims; when `[]`, show none. */
  applicableDims?: string[];
  autogradeData: Record<string, string> | null;
  showAutogradeReasoning?: boolean;
  dimensionCriteria: Record<string, DimensionCriterion[]>;
  disableFade?: boolean;
}

/**
 * Task context preview - Shows collapsible sections for:
 * 1. Task Objectives (Priority 1)
 * 2. AI Analysis (Priority 2)
 * 3. Task Description (Priority 3, collapsed by default)
 */
const TaskContextPreview: React.FC<TaskContextPreviewProps> = ({
  task,
  applicableDims,
  autogradeData,
  showAutogradeReasoning = false,
  dimensionCriteria,
  disableFade = false,
}) => {
  const { evalDimensionKeys, dimensionColors } = useTenantGradingSettings();
  const resolvedApplicableDims = applicableDims ?? evalDimensionKeys;

  const objectives = useMemo(() => {
    if (!task?.analysis?.dimensions) return [];
    const allObjectives: Objective[] = [];

    resolvedApplicableDims.forEach((dimKey) => {
      const dimData = (task.analysis?.dimensions as Record<string, DimObjectives>)?.[dimKey];
      if (dimData?.applicable && dimData?.demands) {
        dimData.demands.forEach((demand: string, idx: number) => {
          allObjectives.push({
            dimension: dimKey,
            text: demand,
            key: `${dimKey}-${idx}`,
            color: dimensionColors[dimKey] || '#666666',
          });
        });
      }
    });
    return allObjectives;
  }, [task?.analysis?.dimensions, resolvedApplicableDims, dimensionColors]);

  const hasObjectives = objectives.length > 0;
  const hasAutogradeData = !!(showAutogradeReasoning && autogradeData && Object.keys(autogradeData).length > 0);

  const [isObjectivesExpanded, setIsObjectivesExpanded] = useState(true);
  const [isAiExpanded, setIsAiExpanded] = useState(true);
  const [isTaskExpanded, setIsTaskExpanded] = useState(!hasObjectives);

  return (
    <VStack space="lg" h={disableFade ? undefined : '100%'} py="$2" mb={disableFade ? 0 : '$4'}>
      {/* 1. Objectives - Top Priority */}
      {hasObjectives && (
        <VStack space="md" flex={isObjectivesExpanded && !disableFade ? 1 : undefined} mb={isObjectivesExpanded && !disableFade ? 0 : '$4'} minHeight={0}>
          <SectionHeader
            icon={AlignLeft}
            title="Objectives"
            isExpanded={isObjectivesExpanded}
            onToggle={() => setIsObjectivesExpanded(!isObjectivesExpanded)}
          />
          {!!isObjectivesExpanded && (
            <ScrollableContent showFade={!disableFade}>
              <ObjectivesList objectives={objectives} />
            </ScrollableContent>
          )}
        </VStack>
      )}

      {/* 2. AI Analysis - Second Priority */}
      {hasAutogradeData && (
        <VStack
          testID="eval-ai-analysis"
          space="md"
          flex={isAiExpanded && !disableFade ? 1 : undefined}
          mb={isAiExpanded && !disableFade ? 0 : '$4'}
          minHeight={0}
        >
          <SectionHeader
            icon={Sparkles}
            iconColor="$brandBase"
            title="AI Analysis"
            isExpanded={isAiExpanded}
            onToggle={() => setIsAiExpanded(!isAiExpanded)}
          />
          {!!isAiExpanded && (
            <ScrollableContent showFade={!disableFade}>
              <AutogradeReasoning
                autogradeData={autogradeData}
                dimensionColors={dimensionColors}
                dimensionCriteria={dimensionCriteria}
                applicableDims={resolvedApplicableDims}
              />
            </ScrollableContent>
          )}
        </VStack>
      )}

      {/* 3. Task Description - Last Priority, collapsed by default */}
      <VStack space="sm" flex={isTaskExpanded && !disableFade ? 1 : undefined} mb={isTaskExpanded && !disableFade ? 0 : '$4'} minHeight={0}>
        <SectionHeader
          icon={NotebookText}
          title="Task Description"
          isExpanded={isTaskExpanded}
          onToggle={() => setIsTaskExpanded(!isTaskExpanded)}
        />
        {!!isTaskExpanded && (
          <ScrollableContent showFade={!disableFade}>
            <HtmlText fontSize={13} content={task.description} />
          </ScrollableContent>
        )}
      </VStack>
    </VStack>
  );
};

export default TaskContextPreview;
